<template>
  <v-row class="ma-3 justify-center">
    <div
      v-if="isLoadingProject"
      class="loader pt-12"
    >
      <v-progress-circular
        :size="70"
        color="#055289"
        indeterminate
      />
    </div>

    <v-card
      v-else-if="didLoadProjectFail"
      class="px-3 col-12 elevation-0"
    >
      <v-card-text class="error-message pa-3">
        <v-row>
          <v-icon>error_outline</v-icon>
        </v-row>

        <v-row class="col-12 header-2">
          {{ didLoadProjectFail.message || 'Error Unknown' }}
        </v-row>
      </v-card-text>
    </v-card>

    <v-card
      v-else-if="!project"
      class="px-3 col-12 elevation-0"
    >
      <v-card-text class="error-message pa-3">
        <v-row>
          <v-icon>error_outline</v-icon>
        </v-row>

        <v-row class="col-12 header-2">
          Not Found
        </v-row>
      </v-card-text>
    </v-card>

    <template v-else>
      <v-col class="col-9">
        <!-- Breadcrumbs navigation -->
        <bread-crumbs class="mb-3" />

        <!-- Header: Client Name + Project Identity -->
        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-text>
            <!-- Client Name (linked) -->
            <v-row v-if="clientName">
              <v-col class="pb-0">
                <router-link
                  :to="organizationHref"
                  class="text-decoration-none"
                >
                  <span class="subtitle-1 font-weight-medium primary--text">
                    {{ clientName }}
                  </span>
                </router-link>
              </v-col>
            </v-row>

            <v-row v-else-if="loadingClient">
              <v-col class="pb-0">
                <v-skeleton-loader
                  type="text"
                  width="200"
                />
              </v-col>
            </v-row>

            <!-- No Client Associated -->
            <v-row v-else>
              <v-col class="pb-0">
                <span class="subtitle-1 font-weight-medium midgray--text font-italic">
                  No client assigned
                </span>
              </v-col>
            </v-row>

            <!-- Project Code -->
            <v-row>
              <v-col class="py-0">
                <span class="body-2 midgray--text">{{ project.projectCode }}</span>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-title class="darkblue--text pt-0 d-flex align-center justify-space-between">
            <span>{{ project.projectName }}</span>
            <v-btn
              v-if="hasAccessToProjectManagement"
              outlined
              small
              class="text-none ml-3"
              @click="editProject"
            >
              <font-awesome-icon
                :icon="['fas', 'pen']"
                size="sm"
                class="mr-1"
              />
              Edit
            </v-btn>
          </v-card-title>

          <!-- Description -->
          <v-card-text v-if="project.projectDescription">
            <v-row>
              <v-col class="body-2 pt-0">
                {{ project.projectDescription }}
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Profile Details -->
        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
            Profile
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <!-- Status -->
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Status
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${isActiveColor}--text`">
                    {{ isActiveLabel }}
                  </v-col>
                </v-row>
              </v-col>

              <!-- Type -->
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Type
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                    {{ projectTypeDisplay }}
                  </v-col>
                </v-row>
              </v-col>

              <!-- Estimated Hours -->
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Estimated Hours
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                    {{ project.projectHours || 'None' }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Docs
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col class="py-0">
                    <template v-if="projectDocs.length">
                      <a
                        v-for="(doc, index) in projectDocs"
                        :key="`profile-doc-${index}`"
                        :href="doc.url"
                        class="d-block primary--text text-decoration-none mb-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ doc.title || doc.url }}
                      </a>
                    </template>
                    <span
                      v-else
                      :class="`font-weight-medium ${nearblackTextClass}`"
                    >None</span>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Start Date -->
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Start Date
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                    {{ project.startDateFormatted || 'None' }}
                  </v-col>
                </v-row>
              </v-col>

              <!-- End Date -->
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    End Date
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                    {{ project.endDateFormatted || 'None' }}
                  </v-col>
                </v-row>
              </v-col>

              <!-- Platforms -->
              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Platforms
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col class="py-0">
                    <template v-if="project.projectPlatforms && project.projectPlatforms.length">
                      <v-chip
                        v-for="platform in project.projectPlatforms"
                        :key="platform"
                        small
                        class="mr-1 mb-1"
                      >
                        {{ platform }}
                      </v-chip>
                    </template>
                    <span
                      v-else
                      :class="`font-weight-medium ${nearblackTextClass}`"
                    >None</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Identifiers -->
        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
            Identifiers
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                sm="6"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    ID
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                    {{ project.projectId }}
                  </v-col>
                </v-row>
              </v-col>

              <v-col
                cols="12"
                sm="6"
              >
                <v-row class="subtitle-2">
                  <v-col class="midgray--text py-1">
                    Code
                  </v-col>
                </v-row>
                <v-row class="body-2 mt-1">
                  <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                    {{ project.projectCode }}
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Time Entries -->
        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-subtitle class="font-weight-bold midgray--text text-uppercase">
            <v-row class="align-center">
              <v-col>
                <span>Time Entries</span>
              </v-col>
              <v-col
                v-if="timeEntries.length"
                class="col-auto text-right"
              >
                <v-btn
                  color="primary"
                  dark
                  small
                  @click.stop="exportTimeEntriesAsCsv"
                >
                  <font-awesome-icon
                    :icon="['fas', 'file-export']"
                    size="sm"
                    class="mr-1"
                  />
                  <span>Export</span>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-subtitle>

          <v-card-text>
            <!-- Loading State -->
            <v-row v-if="loadingTimeEntries">
              <v-col>
                <v-skeleton-loader type="table-heading, table-row@3" />
              </v-col>
            </v-row>

            <template v-else-if="timeEntries.length">
              <!-- Billable Hours Summary -->
              <v-row class="px-2">
                <v-col>
                  <v-row class="subtitle-2">
                    <v-col class="midgray--text">
                      Billable Hours
                    </v-col>
                  </v-row>
                  <v-row class="body-2 mt-0">
                    <v-col :class="`py-0 font-weight-medium ${nearblackTextClass}`">
                      {{ calculatedBillableHours }}
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>

              <!-- Time Entries Table -->
              <v-row>
                <v-col>
                  <v-data-table
                    :headers="timeColumns"
                    :items="timeEntries"
                    dense
                  />
                </v-col>
              </v-row>
            </template>

            <v-row v-else>
              <v-col class="midgray--text body-2">
                No time entries found for this project.
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <project-change-log
          :project="project"
          :text-class="nearblackTextClass"
        />
      </v-col>
    </template>
  </v-row>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import api from '@/store/api.js'
import { organizationPath } from '@/utils/internalPaths'
import themeClasses from '@/mixins/themeClasses'

const { mapActions, mapState } = createNamespacedHelpers('internal')
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapToolsGetters } = createNamespacedHelpers('tools')
const { mapState: mapLayoutState } = createNamespacedHelpers('layout')

export default {
  name: 'Project',
  components: {
    BreadCrumbs: () => import('@/layout/components/BreadCrumbs'),
    ProjectChangeLog: () => import('./ProjectChangeLog.vue'),
  },
  mixins: [themeClasses],
  data() {
    return {
      colorMap: {
        false: 'toasted',
        true: 'midgray',
        'n/a': 'midgray',
        undefined: 'midgray',
      },

      clientData: null,
      loadingClient: false,

      timeEntries: [],
      loadingTimeEntries: false,

      projectWithTimeEntries: {},

      timeSortByKeys: [
        {
          key: 'timeEntryDate',
          direction: 'DSC',
        },
        {
          key: 'personName',
          direction: 'ASC',
        },
      ],
    }
  },
  computed: {
    ...mapAuthGetters(['hasAccessToProjectManagement']),
    ...mapToolsGetters(['projectTypes']),
    nearblackTextClass() {
      return this.themeTextClass('nearblack--text', 'white--text')
    },
    ...mapLayoutState({
      icons: (state) => state.icons,
      valueIcons: (state) => state.icons.values,
    }),
    ...mapState({
      didLoadProjectFail: (state) => state.didLoadProjectFail,
      isLoadingProject: (state) => state.isLoadingProject,
      project: (state) => state.currentProject,
    }),
    effectiveBillingCode() {
      if (this.project?.billingCode) return this.project.billingCode
      const code = this.project?.projectCode
      if (!code) return null
      const match = code.match(/^(.+?)_P/)
      return match ? match[1] : null
    },
    clientName() {
      return this.clientData?.clientName || null
    },
    organizationHref() {
      if (!this.effectiveBillingCode) return '#'
      return organizationPath.profile(this.effectiveBillingCode)
    },

    timeColumns() {
      return [
        { text: 'Date', value: 'timeEntryDateFormatted', sortable: true },
        { text: 'Person', value: 'personName', sortable: true },
        { text: 'Team', value: 'teamName', sortable: true },
        { text: 'Duration (HH:MM)', value: 'durationFormatted', sortable: true },
        { text: 'Classification', value: 'timeClassificationName', sortable: true },
        { text: 'Note', value: 'workNote', sortable: true },
      ]
    },

    // tools/exportAs expects export-column shape ({ columnName, key }), not the
    // Vuetify v-data-table header shape ({ text, value }) timeColumns uses for
    // the on-screen table - passing timeColumns directly produced a CSV of
    // "undefined" headers and cells.
    timeExportColumns() {
      return this.timeColumns.map((column) => ({
        columnName: column.text,
        key: column.value,
      }))
    },

    calculatedBillableHours() {
      const minutesRaw = this.timeEntries.reduce(
        (acc, item) => acc + (item.billableActionDuration || 0),
        0,
      )
      if (minutesRaw === 0) return '0'
      const hours = (minutesRaw / 60).toFixed(2)
      const [wholeHours, decimalPart] = hours.split('.')
      const dec = Number(decimalPart)
      let minutes = ''
      if (dec > 0 && dec <= 25) minutes = ':15'
      else if (dec > 25 && dec <= 50) minutes = ':30'
      else if (dec > 50 && dec <= 75) minutes = ':45'
      return `${wholeHours}${minutes}`
    },

    // Get reportId directly from route params for reliable reactivity
    reportId() {
      return this.$route.params.reportId
    },

    isDeleted() {
      const { project = {} } = this
      const { isDeleted } = project

      return isDeleted
    },
    isActive() {
      const { project = {} } = this
      const { isActive } = project

      return isActive
    },
    isActiveColor() {
      const { isActive, valueIcons } = this
      const status = String(isActive)
      const { color = 'nearblack' } = valueIcons[status] || {}

      return color
    },
    isActiveLabel() {
      if (this.isDeleted) return 'Deleted'
      return this.isActive ? 'Active' : 'Inactive'
    },
    projectTypeMap() {
      return (this.projectTypes || []).reduce((map, type) => {
        map[type.value] = type.title || type.text || type.value
        return map
      }, {})
    },
    projectTypeDisplay() {
      let values = []
      if (Array.isArray(this.project?.projectTypes) && this.project.projectTypes.length > 0) {
        values = this.project.projectTypes.filter(Boolean)
      } else if (this.project?.projectType) {
        values = [this.project.projectType]
      }

      if (values.length === 0) {
        return 'None'
      }

      return values.map((value) => this.projectTypeMap[value] || value).join(', ')
    },
    projectDocs() {
      const docs = Array.isArray(this.project?.docs) ? this.project.docs : []
      return docs
        .map((doc) => ({
          title: doc?.title || doc?.name || '',
          url: doc?.url || doc?.href || '',
        }))
        .filter((doc) => doc.title || doc.url)
    },
  },
  watch: {
    reportId: {
      immediate: true,
      handler(newValue) {
        if (!newValue) return null

        return this.getProject(newValue)
      },
    },
    project: {
      immediate: true,
      handler(newProject) {
        if (!newProject) return

        // Fetch client using effective billing code
        const billingCode = this.effectiveBillingCode
        if (billingCode) {
          this.fetchClient(billingCode)
        }

        // Fetch time entries
        if (newProject.projectId) {
          this.fetchTimeEntries(newProject)
        }

        // Set selectedSearch so breadcrumbs and search bar display correctly
        this.$store.commit('tools/selectedSearch', {
          isSearchOption: true,
          route: 'internal',
          reportKey: 'projects',
          reportId: newProject.projectCode || this.reportId,
          reportName: 'projects',
          billingCode,
          text: newProject.projectName || newProject.projectCode || this.reportId,
          name: newProject.projectName || newProject.projectCode || this.reportId,
          dataType: 'project',
        })
      },
    },
    // Update breadcrumbs with client name once loaded
    clientData: {
      handler(newClientData) {
        if (!newClientData || !this.project) return

        // Update selectedSearch to include parent (client) info for breadcrumbs
        this.$store.commit('tools/selectedSearch', {
          isSearchOption: true,
          route: 'internal',
          reportKey: 'projects',
          reportId: this.project.projectCode || this.reportId,
          reportName: 'projects',
          billingCode: this.effectiveBillingCode,
          text: this.project.projectName || this.project.projectCode || this.reportId,
          name: this.project.projectName || this.project.projectCode || this.reportId,
          dataType: 'project',
          parentName: newClientData.clientName,
          parentHref: this.organizationHref,
        })
      },
    },
  },
  methods: {
    ...mapActions(['getProject']),

    editProject() {
      if (this.project) {
        this.$store.dispatch('internal/onEditProject', this.project)
      }
    },

    async fetchClient(billingCode) {
      this.loadingClient = true
      try {
        const response = await api.getClientByBillingCode(billingCode)
        const clients = response?.content || []
        this.clientData = clients[0] || null
      } catch {
        this.clientData = null
      } finally {
        this.loadingClient = false
      }
    },

    async fetchTimeEntries(project) {
      this.loadingTimeEntries = true
      try {
        const result = await this.setTimeEntriesOnProject(project)
        if (result && !result.error && this.projectWithTimeEntries) {
          this.timeEntries = this.projectWithTimeEntries.timeEntries || []
        } else {
          this.timeEntries = []
        }
      } catch {
        this.timeEntries = []
      } finally {
        this.loadingTimeEntries = false
      }
    },

    exportTimeEntriesAsCsv() {
      return this.$store.dispatch('tools/exportAs', {
        exportColumns: this.timeExportColumns,
        exportFormat: 'csv',
        exportGroups: [{ items: this.timeEntries }],
        exportName: `project-${this.project?.projectCode || 'export'}-time-entries`,
      })
    },

    sortTimeEntriesByDateAsc(a, b) {
      return new Date(a.timeEntryDate) - new Date(b.timeEntryDate)
    },
    sortTimeEntriesByDateDsc(a, b) {
      return new Date(b.timeEntryDate) - new Date(a.timeEntryDate)
    },
    sortTimeEntriesByAsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateAsc
      }

      return (a, b) => {
        if (a[key] < b[key]) {
          return -1
        }

        if (a[key] > b[key]) {
          return 1
        }

        return 0
      }
    },
    sortTimeEntriesByDsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateDsc
      }

      return (a, b) => {
        if (a[key] > b[key]) {
          return -1
        }

        if (a[key] < b[key]) {
          return 1
        }

        return 0
      }
    },

    sortTimeEntries({ direction, key }) {
      return (a, b) => {
        if (direction === 'ASC') {
          return this.sortTimeEntriesByAsc(key)(a, b)
        }

        return this.sortTimeEntriesByDsc(key)(a, b)
      }
    },

    /**
     * @function setTimeEntriesOnProject
     * @desc Fetch and set the time entries on a project.
     * @param project {Object} The project to fetch time entries for.
     */
    async setTimeEntriesOnProject(project) {
      // Fetch the time entries for all projects.
      const timeEntriesResponseFromApi = await this.$store.dispatch('tools/fetchTimeEntriesByProjectId', project.projectId)

      // Handle error response from API.
      if (!timeEntriesResponseFromApi) {
        const message = 'No response received when fetching time entries for projects.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        // @todo Inform the user of the error.
        // Show a notification or message to the user.

        // Exit early with empty time entries.
        return timeEntriesResponseFromApi || { error: true, message }
      }

      // Handle error response from API.
      if (timeEntriesResponseFromApi.error) {
        const message = timeEntriesResponseFromApi.message
          || timeEntriesResponseFromApi.error?.message
          || 'Unknown error occurred when fetching time entries for projects.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        // @todo Inform the user of the error.
        // Show a notification or message to the user.

        // Exit early with empty time entries.
        return timeEntriesResponseFromApi || { error: true, message }
      }

      const timeEntriesFromApi = timeEntriesResponseFromApi.content || []

      // Ensure timeEntriesFromApi is an array before mapping
      if (!Array.isArray(timeEntriesFromApi)) {
        const message = 'Invalid time entries data received from API.'

        // Show a message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        return { error: true, message }
      }

      // Transform each time entry, creating an array of promises.
      // eslint-disable-next-line arrow-body-style
      const timeEntryPromises = timeEntriesFromApi.map((timeEntry) => {
        return this.$store.dispatch('tools/transformTimeItem', timeEntry)
      })

      // Wait for all transformations to complete, resulting in the
      // final time entries array.
      const timeEntries = await Promise.all(timeEntryPromises)

      // Sort the time entries by date (latest first) and then by person name
      // Apply the sorting configurations in reverse order to ensure the user's
      // latest sort order overrides all previous sort orders.
      const sortedTimeEntries = this.timeSortByKeys.reduceRight((sortedArray, sortingConfig) => {
        const sortByKeyAndDirection = this.sortTimeEntries(sortingConfig)
        return sortedArray.sort(sortByKeyAndDirection)
      }, timeEntries)

      // Update projects with its time entries and calculations.
      this.projectWithTimeEntries = await this.$store.dispatch('tools/calculateProjectHours', {
        timeEntries: sortedTimeEntries,
        project,
      })

      return this.projectWithTimeEntries
    },
  },
}
</script>

<style lang="scss" scoped>
.v-card__subtitle {
  letter-spacing: 0.025rem;
}
</style>
